import React from 'react';
import { Alert, Button, Snackbar, AlertColor, SnackbarOrigin } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { ShowSnackbar, SnackbarType, SnackbarMessage, HandleClose } from './types';

const AUTO_HIDE_DURATION = 6000;
const ANCHOR_ORIGIN: SnackbarOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const ALERT_SEVERITY_BY_TYPE: {
  [key in Exclude<SnackbarType, SnackbarType.Regular>]: AlertColor;
} = {
  [SnackbarType.Info]: 'info',
  [SnackbarType.Error]: 'error',
  [SnackbarType.Warning]: 'warning',
  [SnackbarType.Success]: 'success',
};

export let showInstance: ShowSnackbar | undefined;

export const SnackbarContainer = () => {
  const [open, setOpen] = React.useState(false);
  const [messageQueue, setMessageQueue] = React.useState<SnackbarMessage[]>([]);
  const [messageData, setMessageData] = React.useState<SnackbarMessage>();

  React.useEffect(() => {
    if (messageQueue.length > 0 && !messageData) {
      // no message is shown now and we're ready to take the next message from queue
      setMessageData(messageQueue[0]);
      setMessageQueue(messageQueue.slice(1));
      setOpen(true);
    } else if (messageQueue.length > 0 && messageData && open) {
      // we have another message that should be shown
      // let's hide this one and show a new one after that
      setOpen(false);
    }
  }, [messageQueue, messageData, open]);

  const handleClose: HandleClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const handleExited = React.useCallback(() => {
    setMessageData(undefined);
  }, []);

  const show: ShowSnackbar = React.useCallback((type, message, actionData) => {
    setMessageQueue((prev) => [
      ...prev,
      {
        type,
        message,
        actionData,
      },
    ]);
  }, []);

  React.useEffect(() => {
    showInstance = show;

    return () => {
      showInstance = undefined;
    };
  }, [show]);

  const TransitionProps: TransitionProps = React.useMemo(() => {
    return { onExited: handleExited };
  }, [handleExited]);

  if (!messageData || !messageData.type) {
    return null;
  }

  const { type, actionData, message } = messageData;

  if (type === SnackbarType.Regular) {
    const actionNode = actionData ? (
      <Button color='primary' size='small' onClick={actionData.getHandler(handleClose)}>
        {actionData.text}
      </Button>
    ) : undefined;

    return (
      <Snackbar
        open={open}
        autoHideDuration={AUTO_HIDE_DURATION}
        onClose={handleClose}
        message={message}
        TransitionProps={TransitionProps}
        anchorOrigin={ANCHOR_ORIGIN}
        action={actionNode}
      />
    );
  } else {
    const severity = ALERT_SEVERITY_BY_TYPE[type];

    return (
      <Snackbar
        open={open}
        autoHideDuration={AUTO_HIDE_DURATION}
        onClose={handleClose}
        TransitionProps={TransitionProps}
        anchorOrigin={ANCHOR_ORIGIN}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%', borderRadius: 0.5 }}>
          {message}
        </Alert>
      </Snackbar>
    );
  }
};
